import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useThemeColors } from '../context/ThemeContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { getDeviceId } from '../utils/deviceId';
import EmailCodeModal from './EmailCodeModal';

const SESSION_OK = 'mekafix_2fa_ok';

// Gate de 2FA para PROVEEDORES (mecánico/taller). Obligatorio cuando el interruptor
// config/security.twoFactorProviders === true. Recuerda el equipo (deviceId) para
// no re-pedir el código en el mismo navegador. Inerte si el interruptor está apagado
// o si el usuario no es proveedor. El acceso real lo da Firebase Auth; esto añade
// una capa de verificación por correo (candado a nivel de app).
export default function TwoFactorGate({ children }) {
  const { t } = useTranslation();
  const { user, userProfile, realRole, profileLoaded, refreshProfile, logout } = useAuth();
  const { isDark } = useThemeColors();
  const tk = isDark ? dsv2 : dsv2Light;

  const [enforce, setEnforce] = useState(null); // null = sin cargar
  // 2FA obligatorio para PROVEEDORES (mecánico/taller) y para cuentas con
  // IDENTIDAD VERIFICADA: una cuenta verificada debe tener segundo factor.
  const requires2fa = realRole === 'mechanic' || realRole === 'workshop_owner' || userProfile?.idVerified === true;
  const deviceId = getDeviceId();

  useEffect(() => {
    let alive = true;
    // Solo consultamos el interruptor para cuentas que requieren 2FA.
    if (!user || !requires2fa) { setEnforce(false); return; }
    getDoc(doc(db, 'config', 'security'))
      .then(s => { if (alive) setEnforce(s.exists() && s.data().twoFactorProviders === true); })
      .catch(() => { if (alive) setEnforce(false); }); // ante error, NO bloquear
    return () => { alive = false; };
  }, [user, requires2fa]);

  const trusted = Array.isArray(userProfile?.twoFactorDevices) && userProfile.twoFactorDevices.includes(deviceId);
  const passedSession = (() => { try { return sessionStorage.getItem(SESSION_OK) === '1'; } catch { return false; } })();

  // No requiere 2FA / no logueado / interruptor apagado / equipo de confianza → app normal.
  if (!user || !requires2fa || enforce === false || trusted || passedSession) return children;
  // Esperando saber si hay que forzar (evita parpadeo del app antes del gate).
  if (enforce === null || !profileLoaded) {
    return (
      <div style={{ minHeight: '100vh', background: tk.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 34, height: 34, border: `3px solid ${tk.outline}`, borderTopColor: tk.red, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  const onSuccess = async () => {
    try { sessionStorage.setItem(SESSION_OK, '1'); } catch { /* ignore */ }
    try { await refreshProfile(); } catch { /* best-effort */ }
  };

  return (
    <div style={{ minHeight: '100vh', background: tk.bg }}>
      <EmailCodeModal
        purpose="login_2fa"
        deviceId={deviceId}
        title={t('twofa.modalTitle')}
        description={t('twofa.modalDesc')}
        onSuccess={onSuccess}
      />
      <button
        onClick={logout}
        style={{
          position: 'fixed', bottom: 'calc(20px + env(safe-area-inset-bottom))', left: '50%',
          transform: 'translateX(-50%)', zIndex: 12050, background: 'none', border: 'none',
          color: tk.textMuted, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 8,
        }}
      >
        {t('twofa.logout')}
      </button>
    </div>
  );
}
